import {
  MobileHeader,
  RestrictedSessionAlertBar,
  ScheduledForDeletionAlertBar,
} from '@sparklayer-io/dashboard';
import { platformLabel } from '@sparklayer-io/spark-dash-core/context/env-context';
import { useEventListener } from '@sparklayer-io/spark-dash-ui/hooks/use-event-listener';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Outlet } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useScrollTopOnNavigate } from '../lib/use-scroll-top-on-navigate';
import { useHostSession } from './page-host';
import { Sidebar } from './sidebar';
import { acronymOf } from './sidebar/helpers';

export type LayoutProps = {
  fullBleed?: boolean;
  // NOTE: this is to support SettingsThemeEditor in packages/pages
  fullHeight?: boolean;
  sidebar?: ReactNode;
  children?: ReactNode;
};

export function Layout({ fullBleed, fullHeight, sidebar, children }: LayoutProps) {
  const { env, user } = useHostSession();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const toggleSidebar = () => setSidebarOpen((open) => !open);

  useScrollTopOnNavigate(scrollContainerRef);

  // Another tab switched account.
  useEventListener(window, 'storage', (event) => {
    if (event.key === 'teamId') {
      window.location.reload();
    }
  });

  useEffect(() => {
    document.querySelector('body')?.classList.add('overflow-hidden');
    return () => document.querySelector('body')?.classList.remove('overflow-hidden');
  }, []);

  return (
    // the top padding is to account for the mobile header height
    <div className="flex h-screen flex-col overflow-hidden pt-12 lg:pt-0">
      <RestrictedSessionAlertBar
        show={user?.autoLoginRestrictedSession ?? false}
        platformName={platformLabel(env?.connected_platform)}
      />
      <ScheduledForDeletionAlertBar show={env?.team.is_scheduled_for_deletion ?? false} />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Sidebar sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} />

        <main
          className="relative flex min-h-0 w-0 flex-1 flex-col overflow-hidden bg-spark-surface-page focus:outline-hidden"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: focusable so keyboard users can scroll the page content
          tabIndex={0}
        >
          <MobileHeader
            acronymTeamName={env?.team.name ? acronymOf(env.team.name) : undefined}
            onToggleSidebar={toggleSidebar}
          />

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
            {sidebar}

            <div ref={scrollContainerRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto">
              <div
                className={cn(
                  !fullBleed &&
                    'mx-auto flex w-full max-w-7xl flex-col gap-12 px-4 pt-4 pb-24 sm:mt-12 sm:pt-0 lg:px-8',
                  fullHeight && 'h-full',
                )}
              >
                {children ?? <Outlet />}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
